// 材料：新建时本地选文件（建完任务再统一上传）；详情页已上传列表 + 补传新版本（带进度）
import { useState } from "react";
import { api } from "../lib/api";
import type { ManifestFileReq, TaskFileDTO } from "@csp/shared";
import { useMediaToken } from "../lib/media";
import { fmtDateTime, fmtSize, fmtTime } from "../lib/format";
import { acceptAttr } from "../lib/manifest";
import { mediaUrl } from "../lib/net";
import { isJunkFile, uploadName, uploadTaskFile } from "../lib/upload";
import { Badge } from "./Badges";

export interface UploadProgress {
  name: string;
  pct: number;
  state: "uploading" | "done" | "error";
  error?: string;
}

/** 新建/编辑任务：每个材料项一个选择框（multiple 时可多选） */
export function MaterialsPicker({
  reqs,
  selected,
  uploaded,
  onSelect,
  progress,
  showMissing,
}: {
  reqs: ManifestFileReq[];
  selected: Record<string, File[]>;
  uploaded: TaskFileDTO[];
  onSelect: (key: string, files: File[]) => void;
  progress: Record<string, UploadProgress[]>;
  showMissing: boolean;
}) {
  const items: ManifestFileReq[] = [...reqs, { key: "other", label: "其他材料（可选）", required: false, multiple: true }];
  return (
    <div>
      {items.map((r) => {
        const have = uploaded.filter((f) => f.key === r.key);
        const sel = selected[r.key] ?? [];
        const missing = showMissing && r.required && !have.some((f) => f.state === "synced") && sel.length === 0;
        return (
          <div className="upload-item" key={r.key} style={missing ? { borderColor: "var(--danger)" } : undefined}>
            <div className="row">
              <b>
                {r.label}
                {r.required && <span className="req">*</span>}
              </b>
              {r.accept?.length ? <span className="small muted">（{r.accept.join(" / ")}）</span> : null}
              {r.multiple && <span className="small muted">可选多份</span>}
            </div>
            <input
              type="file"
              data-key={r.key}
              aria-label={`上传 ${r.label}`}
              multiple={!!r.multiple}
              accept={acceptAttr(r.accept)}
              onChange={(e) => onSelect(r.key, Array.from(e.target.files ?? []))}
              style={{ marginTop: 8 }}
            />
            {have.length > 0 && (
              <div className="files-list muted">已上传：{have.map((f) => `${f.name}（v${f.version}）`).join("、")}</div>
            )}
            {(progress[r.key] ?? []).map((p) => (
              <div key={p.name} className="files-list">
                {p.name}：{p.state === "done" ? <span className="ok-text">已上传 ✓</span> : p.state === "error" ? <span className="err">失败：{p.error}</span> : `${p.pct}%`}
              </div>
            ))}
            {missing && <span className="field-err">缺这份材料</span>}
          </div>
        );
      })}
    </div>
  );
}

/** 详情页：已上传文件列表 + 补传新版本 */
export function MaterialsPanel({
  taskId,
  reqs,
  files,
  canUpload,
  onUploaded,
  canRemove = false,
  onBusy,
}: {
  taskId: string;
  reqs: ManifestFileReq[];
  files: TaskFileDTO[];
  canUpload: boolean;
  onUploaded: () => void;
  canRemove?: boolean;
  onBusy?: (busy: boolean) => void;
}) {
  const token = useMediaToken();
  const [key, setKey] = useState(reqs[0]?.key ?? "other");
  const [prog, setProg] = useState<UploadProgress | null>(null);
  const [busy, setBusy] = useState(false);
  const labelOf = (k: string) => reqs.find((r) => r.key === k)?.label ?? (k === "other" ? "其他材料" : k);
  const req = reqs.find((r) => r.key === key);

  const upload = async (list: FileList | null) => {
    const picked = Array.from(list ?? []).filter((f) => !isJunkFile(f));
    if (!picked.length) return;
    setBusy(true); onBusy?.(true);
    // 整个文件夹上传：一份失败不中断其余，最后汇总
    const failed: string[] = [];
    try { for (const [i, file] of picked.entries()) {
      const name = uploadName(file);
      const label = picked.length > 1 ? `（${i + 1}/${picked.length}）${name}` : name;
      setProg({ name: label, pct: 0, state: "uploading" });
      try {
        await uploadTaskFile(taskId, key, file, (l, t) => setProg({ name: label, pct: Math.round((l / t) * 100), state: "uploading" }));
        setProg({ name: label, pct: 100, state: "done" });
      } catch (e) {
        failed.push(`${name}：${(e as Error).message}`);
      }
    }
    if (failed.length) setProg({ name: `${failed.length} 份失败`, pct: 0, state: "error", error: failed.join("；") });
    } finally { setBusy(false); onBusy?.(false); onUploaded(); }
  };
  const remove = async (id: string) => {
    setBusy(true); onBusy?.(true);
    try { await api.removeFile(id); }
    catch (e) { setProg({ name: "", pct: 0, state: "error", error: (e as Error).message }); }
    finally { setBusy(false); onBusy?.(false); onUploaded(); }
  };

  return (
    <div>
      {files.length === 0 ? (
        <p className="muted">还没有上传材料。</p>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>材料项</th>
              <th>文件</th>
              <th>版本</th>
              <th>sha256</th>
              <th>大小</th>
              <th>上传时间</th>
              {canRemove && <th>操作</th>}
            </tr>
          </thead>
          <tbody>
            {files.map((f) => (
              <tr key={f.id}>
                <td>{labelOf(f.key)}</td>
                <td>
                  {token ? (
                    <a href={mediaUrl(`/api/files/${f.id}`, token)} target="_blank" rel="noreferrer">
                      {f.name}
                    </a>
                  ) : (
                    f.name
                  )}
                  {f.state !== "synced" && (
                    <>
                      {" "}
                      <Badge tone={f.state === "failed" ? "red" : "amber"}>{f.state === "failed" ? "同步失败" : "处理中"}</Badge>
                    </>
                  )}
                </td>
                <td>v{f.version}</td>
                <td className="mono">{f.sha256 ? f.sha256.slice(0, 12) : "—"}</td>
                <td className="nowrap">{fmtSize(f.size)}</td>
                <td className="nowrap" title={fmtDateTime(f.uploadedAt)}>{fmtTime(f.uploadedAt)}</td>
                {canRemove && <td><button className="btn" disabled={busy} aria-label={`删除 ${f.name}`} onClick={() => void remove(f.id)}>删除</button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {canUpload && (
        <div className="row" style={{ marginTop: 12 }}>
          <span>补传 / 替换：</span>
          <select value={key} onChange={(e) => setKey(e.target.value)} style={{ width: "auto" }} aria-label="材料项">
            {reqs.map((r) => (
              <option key={r.key} value={r.key}>{r.label}</option>
            ))}
            <option value="other">其他材料</option>
          </select>
          <input
            type="file"
            disabled={busy}
            aria-label="补传文件"
            multiple={key === "other" || !!req?.multiple}
            accept={acceptAttr(req?.accept)}
            onChange={(e) => {
              void upload(e.target.files);
              e.target.value = "";
            }}
          />
          {(key === "other" || !!req?.multiple) && (
            <label className="btn" style={busy ? { opacity: 0.5, pointerEvents: "none" } : undefined}>
              上传文件夹（含子文件夹）
              <input
                type="file"
                hidden
                disabled={busy}
                aria-label="上传文件夹"
                {...{ webkitdirectory: "", directory: "" }}
                onChange={(e) => {
                  void upload(e.target.files);
                  e.target.value = "";
                }}
              />
            </label>
          )}
          {prog && (
            <span className="small">
              {prog.name}：{prog.state === "done" ? <span className="ok-text">已上传 ✓</span> : prog.state === "error" ? <span className="err">{prog.error}</span> : `${prog.pct}%`}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
